/**
 * 用户色板预设：依赖 Supabase 表 public.lighting_presets（见 supabase/migrations/001_lighting_presets.sql）
 *
 * 未建表时请求会 404 并污染控制台。默认不访问该表；建表后在 .env 设置 VITE_ENABLE_LIGHTING_PRESETS=true 并重新构建。
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export function isLightingPresetsFeatureEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_LIGHTING_PRESETS === 'true';
}

function isTableMissingError(err: { message?: string; code?: string }): boolean {
  const m = (err.message || '').toLowerCase();
  return (
    err.code === 'PGRST205' ||
    m.includes('schema cache') ||
    m.includes('does not exist') ||
    (m.includes('lighting_presets') && m.includes('could not find'))
  );
}

export type LightingPresetPayload = {
  hexColors: string[];
  summary?: string;
  colorTempBias?: number;
  saturationDelta?: number;
  /** 对比/明暗反差 → 与渲染 intensity 联动 */
  intensityDelta?: number;
};

export type LightingPresetRow = {
  id: string;
  name: string;
  palette: LightingPresetPayload;
  created_at?: string;
};

export async function fetchUserLightingPresets(
  supabase: SupabaseClient,
  userId: string,
): Promise<LightingPresetRow[]> {
  if (!isLightingPresetsFeatureEnabled()) {
    return [];
  }

  const { data, error } = await supabase
    .from('lighting_presets')
    .select('id,name,palette,created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    if (isTableMissingError(error)) {
      if (import.meta.env.DEV) {
        console.debug(
          '[lighting_presets] 表未创建或 PostgREST 未加载该表，已跳过列表（执行 supabase/migrations/001_lighting_presets.sql 并设 VITE_ENABLE_LIGHTING_PRESETS=true）',
        );
      }
      return [];
    }
    console.warn('[lighting_presets] list failed:', error.message);
    return [];
  }
  return (data || []) as LightingPresetRow[];
}

export async function saveLightingPreset(
  supabase: SupabaseClient,
  userId: string,
  name: string,
  palette: LightingPresetPayload,
): Promise<{ ok: boolean; error?: string }> {
  if (!isLightingPresetsFeatureEnabled()) {
    return {
      ok: false,
      error:
        '云存储色板未开启：请先在 Supabase 执行 lighting_presets 建表 SQL，并在前端环境变量设置 VITE_ENABLE_LIGHTING_PRESETS=true 后重新构建。',
    };
  }

  const { error } = await supabase.from('lighting_presets').insert({
    user_id: userId,
    name: name.trim() || '未命名色板',
    palette,
  });

  if (error) {
    if (isTableMissingError(error)) {
      return {
        ok: false,
        error:
          '数据库中尚无 lighting_presets 表，请在 Supabase SQL 编辑器执行项目内 supabase/migrations/001_lighting_presets.sql。',
      };
    }
    console.error('[lighting_presets] insert failed:', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}
